import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, InputBase, Typography, useTheme } from "@mui/material";
import AdaptiveDialog from "../../common/AdaptiveDialog";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import { useCreateWatchlist } from "./hooks/useCreateWatchlist";
import { Watchlist } from "./hooks/useWatchlists";
import { hapticError, hapticSuccess } from "../../../lib/haptics";

const MAX_NAME_LENGTH = 100;
// The counter is noise at "0/100"; it only earns its place near the ceiling.
const COUNTER_VISIBLE_FROM = 70;

interface WatchlistNameModalProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (watchlist: Watchlist) => void;
}

/**
 * Names a new watchlist.
 *
 * Renaming is deliberately not handled here — it happens inline in the manage
 * modal, where the name is already on screen, rather than by stacking a second
 * modal on top of the first.
 */
const WatchlistNameModal: React.FC<WatchlistNameModalProps> = ({ open, onClose, onCreated }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();
  const hairline = isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)";

  const createWatchlist = useCreateWatchlist();
  const [name, setName] = useState("");

  // The surface keeps its state between opens, so clear the field every time it
  // opens rather than showing the previous attempt's value.
  useEffect(() => {
    if (open) {
      setName("");
      createWatchlist.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !createWatchlist.isPending;

  const handleClose = () => {
    if (!createWatchlist.isPending) onClose();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;

    createWatchlist.mutate(
      { name: trimmedName },
      {
        onSuccess: (created) => {
          hapticSuccess();
          onCreated?.(created);
          onClose();
        },
        onError: () => hapticError(),
      },
    );
  };

  return (
    <AdaptiveDialog
      open={open}
      onClose={handleClose}
      title="New watchlist"
      subtitle="Group stocks by theme, sector, or however you think about them."
      maxWidth="xs"
      actions={
        <>
          <Button
            type="submit"
            form="watchlist-name-form"
            disabled={!canSubmit}
            variant="contained"
            fullWidth={isTouch}
            disableElevation
            startIcon={
              createWatchlist.isPending ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              order: isTouch ? 0 : 1,
              minWidth: 96,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              borderRadius: 1.5,
              backgroundColor: "#22c55e",
              "&:hover": { backgroundColor: "#16a34a" },
              // A grey slab reads as broken; a quiet tint reads as "not yet".
              "&.Mui-disabled": {
                backgroundColor: "rgba(34, 197, 94, 0.15)",
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
              },
            }}
          >
            Create
          </Button>
          <Button
            onClick={handleClose}
            disabled={createWatchlist.isPending}
            fullWidth={isTouch}
            sx={{
              order: isTouch ? 1 : 0,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.85rem",
              borderRadius: 1.5,
              color: theme.palette.text.secondary,
              "&:hover": {
                backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
              },
            }}
          >
            Cancel
          </Button>
        </>
      }
    >
      <Box component="form" id="watchlist-name-form" onSubmit={handleSubmit}>
        <InputBase
          autoFocus
          fullWidth
          value={name}
          onChange={(event) => setName(event.target.value)}
          disabled={createWatchlist.isPending}
          placeholder="Name this watchlist"
          inputProps={{ maxLength: MAX_NAME_LENGTH, "aria-label": "Watchlist name" }}
          sx={{
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            border: `1px solid ${createWatchlist.error ? theme.palette.error.main : hairline}`,
            backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
            fontSize: "0.9rem",
            fontWeight: 500,
            color: theme.palette.text.primary,
            transition: "border-color 0.18s ease, background-color 0.18s ease",
            "&.Mui-focused": { borderColor: "rgba(34, 197, 94, 0.5)" },
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1.5,
            minHeight: 18,
            mt: 0.75,
          }}
        >
          {createWatchlist.error && (
            <Typography sx={{ fontSize: "0.78rem", color: theme.palette.error.main }}>
              {createWatchlist.error.message}
            </Typography>
          )}
          {!createWatchlist.error && <Box />}

          {name.length >= COUNTER_VISIBLE_FROM && (
            <Typography
              sx={{
                flex: "none",
                fontSize: "0.72rem",
                fontVariantNumeric: "tabular-nums",
                color:
                  name.length === MAX_NAME_LENGTH
                    ? theme.palette.error.main
                    : theme.palette.text.secondary,
              }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          )}
        </Box>
      </Box>
    </AdaptiveDialog>
  );
};

export default WatchlistNameModal;
